'use client';
import React, { useState } from 'react';
import { Camera, MapPin, User, Calendar, CheckCircle, Clock, AlertTriangle, Upload, Plus } from 'lucide-react';

interface Inspection {
  id: string;
  parcelId: string;
  address: string;
  county: string;
  assignedTo: string;
  assignedDate: string;
  dueDate: string;
  status: 'assigned' | 'in-progress' | 'completed' | 'overdue';
  priority: 'high' | 'medium' | 'low';
  findings?: string;
  photos: number;
  geoTagged: boolean;
  completedDate?: string;
}

const INSPECTIONS: Inspection[] = [
  {
    id: 'INS-2024-0081', parcelId: 'LR-NIM-0008', address: 'Ganta Market Zone B', county: 'Nimba',
    assignedTo: 'Officer J. Kollie', assignedDate: '2024-04-01', dueDate: '2024-04-15',
    status: 'completed', priority: 'high', findings: 'Unregistered commercial structure confirmed. 3 stalls operating without tax ID. Parcel boundary encroaches on public road by 4m.',
    photos: 6, geoTagged: true, completedDate: '2024-04-12',
  },
  {
    id: 'INS-2024-0082', parcelId: 'LR-LOF-0019', address: 'Voinjama Riverside', county: 'Lofa',
    assignedTo: 'Officer M. Pewee', assignedDate: '2024-04-05', dueDate: '2024-04-20',
    status: 'in-progress', priority: 'high', photos: 2, geoTagged: true,
  },
  {
    id: 'INS-2024-0083', parcelId: 'LR-MAR-0022', address: 'Kakata Industrial Blvd', county: 'Margibi',
    assignedTo: 'Officer T. Flomo', assignedDate: '2024-04-08', dueDate: '2024-04-22',
    status: 'assigned', priority: 'medium', photos: 0, geoTagged: false,
  },
  {
    id: 'INS-2024-0084', parcelId: 'LR-SIN-0007', address: 'Greenville Port Area', county: 'Sinoe',
    assignedTo: 'Officer B. Kpehe', assignedDate: '2024-03-15', dueDate: '2024-03-30',
    status: 'overdue', priority: 'high', photos: 0, geoTagged: false,
  },
  {
    id: 'INS-2024-0085', parcelId: 'LR-GGH-0011', address: 'Zwedru Central Market', county: 'Grand Gedeh',
    assignedTo: 'Officer S. Wreh', assignedDate: '2024-04-10', dueDate: '2024-04-25',
    status: 'completed', priority: 'low', findings: 'Property confirmed registered. Minor boundary discrepancy noted — 0.5m encroachment on adjacent plot. Owner notified.',
    photos: 4, geoTagged: true, completedDate: '2024-04-18',
  },
];

const statusConfig = {
  assigned: { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)', label: 'Assigned', icon: User },
  'in-progress': { color: '#fbbf24', bg: 'rgba(251,191,36,0.1)', label: 'In Progress', icon: Clock },
  completed: { color: '#34d399', bg: 'rgba(52,211,153,0.1)', label: 'Completed', icon: CheckCircle },
  overdue: { color: '#f87171', bg: 'rgba(248,113,113,0.1)', label: 'Overdue', icon: AlertTriangle },
};

const priorityConfig = {
  high: { color: '#f87171', label: 'High' },
  medium: { color: '#fbbf24', label: 'Medium' },
  low: { color: '#34d399', label: 'Low' },
};

export default function FieldInspectionPanel() {
  const [selectedInspection, setSelectedInspection] = useState<Inspection | null>(INSPECTIONS[0]);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const filtered = INSPECTIONS.filter((i) => filterStatus === 'all' || i.status === filterStatus);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Left: Inspection list */}
      <div className="lg:col-span-2 space-y-3">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: 'Total', value: INSPECTIONS.length, color: 'var(--accent)' },
            { label: 'In Progress', value: INSPECTIONS.filter((i) => i.status === 'in-progress').length, color: '#fbbf24' },
            { label: 'Completed', value: INSPECTIONS.filter((i) => i.status === 'completed').length, color: '#34d399' },
            { label: 'Overdue', value: INSPECTIONS.filter((i) => i.status === 'overdue').length, color: '#f87171' },
          ].map((s) => (
            <div key={s.label} className="rounded-xl p-3 text-center" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
              <p className="text-lg font-bold font-mono-data" style={{ color: s.color }}>{s.value}</p>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filter + New */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 flex-wrap">
            {['all', 'assigned', 'in-progress', 'completed', 'overdue'].map((f) => (
              <button
                key={f}
                onClick={() => setFilterStatus(f)}
                className="px-2.5 py-1 rounded-full text-xs font-medium transition-all capitalize"
                style={{
                  backgroundColor: filterStatus === f ? 'rgba(212,160,23,0.2)' : 'var(--dark-elevated)',
                  color: filterStatus === f ? 'var(--accent)' : 'var(--dark-muted)',
                  border: `1px solid ${filterStatus === f ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                }}
              >
                {f === 'all' ? 'All' : f}
              </button>
            ))}
          </div>
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)', color: '#070e1f' }}>
            <Plus size={12} />
            Assign Inspection
          </button>
        </div>

        {/* Inspection cards */}
        <div className="space-y-2">
          {filtered.map((insp) => {
            const cfg = statusConfig[insp.status];
            const StatusIcon = cfg.icon;
            const priCfg = priorityConfig[insp.priority];
            const isSelected = selectedInspection?.id === insp.id;

            return (
              <button
                key={insp.id}
                onClick={() => setSelectedInspection(insp)}
                className="w-full text-left p-3 rounded-xl transition-all"
                style={{
                  backgroundColor: isSelected ? 'rgba(212,160,23,0.08)' : 'var(--dark-card)',
                  border: `1px solid ${isSelected ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold font-mono-data" style={{ color: 'var(--accent)' }}>{insp.id}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: priCfg.color + '20', color: priCfg.color }}>
                      {priCfg.label}
                    </span>
                  </div>
                  <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                    <StatusIcon size={10} />
                    {cfg.label}
                  </span>
                </div>
                <p className="text-xs font-medium mb-0.5" style={{ color: 'var(--dark-text)' }}>{insp.address}</p>
                <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--dark-muted)' }}>
                  <span className="flex items-center gap-1"><User size={10} />{insp.assignedTo}</span>
                  <span className="flex items-center gap-1"><MapPin size={10} />{insp.county}</span>
                  <span className="flex items-center gap-1"><Calendar size={10} />Due: {insp.dueDate}</span>
                </div>
                {insp.photos > 0 && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <Camera size={10} style={{ color: '#60a5fa' }} />
                    <span className="text-xs" style={{ color: '#60a5fa' }}>{insp.photos} photos</span>
                    {insp.geoTagged && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(52,211,153,0.1)', color: '#34d399' }}>
                        Geo-tagged
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Right: Inspection detail */}
      <div>
        {selectedInspection ? (
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
            <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--dark-border)' }}>
              <p className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>Inspection Detail</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{selectedInspection.id}</p>
            </div>
            <div className="p-4 space-y-3">
              <div className="space-y-2">
                {[
                  { label: 'Parcel ID', value: selectedInspection.parcelId },
                  { label: 'Address', value: selectedInspection.address },
                  { label: 'County', value: selectedInspection.county },
                  { label: 'Assigned To', value: selectedInspection.assignedTo },
                  { label: 'Assigned Date', value: selectedInspection.assignedDate },
                  { label: 'Due Date', value: selectedInspection.dueDate },
                  ...(selectedInspection.completedDate ? [{ label: 'Completed', value: selectedInspection.completedDate }] : []),
                ].map((row) => (
                  <div key={row.label} className="flex justify-between text-xs py-1 border-b" style={{ borderColor: 'var(--dark-border)' }}>
                    <span style={{ color: 'var(--dark-muted)' }}>{row.label}</span>
                    <span style={{ color: 'var(--dark-text)' }}>{row.value}</span>
                  </div>
                ))}
              </div>

              {selectedInspection.findings && (
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: 'var(--dark-text)' }}>Field Findings</p>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--dark-muted)' }}>{selectedInspection.findings}</p>
                </div>
              )}

              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 text-xs" style={{ color: '#60a5fa' }}>
                  <Camera size={12} />
                  {selectedInspection.photos} photos captured
                </div>
                {selectedInspection.geoTagged && (
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(52,211,153,0.1)', color: '#34d399' }}>
                    ✓ Geo-tagged
                  </span>
                )}
              </div>

              {selectedInspection.status !== 'completed' && (
                <button className="w-full py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all hover:opacity-90"
                  style={{ backgroundColor: 'rgba(96,165,250,0.15)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.3)' }}>
                  <Upload size={12} />
                  Upload Field Report
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-xl p-6 text-center" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
            <Camera size={32} className="mx-auto mb-2" style={{ color: 'var(--dark-muted)' }} />
            <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>Select an inspection to view details</p>
          </div>
        )}
      </div>
    </div>
  );
}
